// frontend/src/pages/ForgotPassword.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { requestPasswordReset, resetPassword } from '../services/api';
import Logo from '../components/Logo';

const RULES = [
  { label: 'At least 10 characters', test: p => p.length >= 10 },
  { label: 'An uppercase letter',    test: p => /[A-Z]/.test(p) },
  { label: 'A lowercase letter',     test: p => /[a-z]/.test(p) },
  { label: 'A number',               test: p => /[0-9]/.test(p) },
  { label: 'A symbol',               test: p => /[^A-Za-z0-9]/.test(p) },
];

const inputStyle = {
  width: '100%', padding: '9px 12px', fontSize: 13,
  border: '0.5px solid rgba(0,0,0,0.14)', borderRadius: 8,
  outline: 'none', color: '#1A1A18',
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: '#5F5E5A' };
const btnStyle = (disabled) => ({
  width: '100%', padding: '10px', background: '#1F3B68', color: '#fff',
  border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1,
  fontFamily: "'Poppins', sans-serif",
});

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep]         = useState(1);       // 1 = request, 2 = reset, 3 = done
  const [email, setEmail]       = useState('');
  const [code, setCode]         = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [info, setInfo]         = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const allRulesPass = RULES.every(r => r.test(password));
  const matches      = password.length > 0 && password === confirm;
  const canReset     = code.trim().length === 6 && allRulesPass && matches && !loading;

  const submitRequest = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await requestPasswordReset(email);
      setInfo(res.message || 'If an account with that email exists, a reset code has been sent.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setError(''); setLoading(true);
    try {
      await requestPasswordReset(email);
      setInfo('A new code has been sent if the account exists.');
    } catch {
      setError('Could not resend the code. Please try again.');
    } finally { setLoading(false); }
  };

  const submitReset = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await resetPassword(email, code.trim(), password);
      setStep(3);
    } catch (err) {
      setInfo('');
      setError(err.response?.data?.error || 'Could not reset your password. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0E1B31 0%, #1F3B68 60%, #2C4F86 100%)',
    }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
          <Logo size={34} variant="light" to={null} tagline="Performance Management" />
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
          <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 18, marginBottom: 22 }}>
            {step === 3 ? 'Password updated' : 'Reset your password'}
          </h2>

          {error && (
            <div style={{ background: '#FCEBEB', color: '#A32D2D', fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 16 }}>
              {error}
            </div>
          )}
          {info && step === 2 && (
            <div style={{ background: '#EAF3EC', color: '#2E6B3E', fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 16 }}>
              {info}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={submitRequest}>
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@energin.com.au" required autoFocus autoComplete="email" style={inputStyle} />
              </div>
              <button type="submit" disabled={loading} style={btnStyle(loading)}>
                {loading ? 'Sending…' : 'Send reset code'}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitReset}>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>6-digit code</label>
                <input type="text" inputMode="numeric" value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456" required autoFocus autoComplete="one-time-code" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>New password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required autoComplete="new-password" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Confirm new password</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••" required autoComplete="new-password" style={inputStyle} />
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', fontSize: 12 }}>
                {RULES.map(r => {
                  const ok = r.test(password);
                  return (
                    <li key={r.label} style={{ color: ok ? '#2E6B3E' : '#8A8A86', marginBottom: 3 }}>
                      <span style={{ marginRight: 6 }}>{ok ? '✓' : '○'}</span>{r.label}
                    </li>
                  );
                })}
                <li style={{ color: matches ? '#2E6B3E' : '#8A8A86' }}>
                  <span style={{ marginRight: 6 }}>{matches ? '✓' : '○'}</span>Passwords match
                </li>
              </ul>

              <button type="submit" disabled={!canReset} style={btnStyle(!canReset)}>
                {loading ? 'Updating…' : 'Reset password'}
              </button>

              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <button type="button" onClick={resend} disabled={loading}
                  style={{ background: 'none', border: 'none', fontSize: 12, color: '#1F3B68', cursor: 'pointer' }}>
                  Resend code
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div>
              <div style={{ background: '#EAF3EC', color: '#2E6B3E', fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 18 }}>
                Your password has been updated. You can now sign in.
              </div>
              <button type="button" onClick={() => navigate('/login')} style={btnStyle(false)}>
                Go to sign in
              </button>
            </div>
          )}

          {step !== 3 && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Link to="/login" style={{ fontSize: 12, color: '#1F3B68', textDecoration: 'none' }}>Back to sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
